/**
 * Exam date/time formatting utilities.
 *
 * All exam times are stored in UTC. These helpers display them in the
 * student's local timezone WITH a timezone label, and optionally show
 * the IST equivalent for international students (since exams are
 * typically created in IST by Indian instructors).
 */

/** Get the short timezone abbreviation for the user's browser (e.g. "IST", "EST", "SAST") */
function getLocalTzAbbr(): string {
  // For Indian timezone, always return "IST" (browsers may return "GMT+5:30")
  if (isIST()) return 'IST';

  try {
    const parts = new Intl.DateTimeFormat('en', { timeZoneName: 'short' }).formatToParts(new Date());
    const raw = parts.find(p => p.type === 'timeZoneName')?.value || '';
    // Some browsers return "GMT+X" — keep it as-is, it's still informative
    return raw;
  } catch {
    return '';
  }
}

/** Check if the user's local timezone is IST (Asia/Kolkata) */
function isIST(): boolean {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta';
}

/** Format a date in IST */
function formatInIST(date: Date, includeDate: boolean): string {
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    ...(includeDate ? { day: 'numeric', month: 'short', year: 'numeric' } : {}),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }) + ' IST';
}

/**
 * Format exam date+time for student display.
 * Shows local timezone + IST reference for non-Indian students.
 *
 * Examples:
 *   Indian student:  "9 Apr 2026, 12:01 AM IST"
 *   Saudi student:   "8 Apr 2026, 9:31 PM AST (12:01 AM IST)"
 *   US student:      "8 Apr 2026, 2:31 PM EDT (9 Apr, 12:01 AM IST)"
 */
export function formatExamDateTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  const tzAbbr = getLocalTzAbbr();

  const localStr = date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (isIST()) {
    return `${localStr} IST`;
  }

  // For non-IST users, append IST equivalent
  const istStr = formatInIST(date, true);
  return `${localStr} ${tzAbbr} (${istStr})`;
}

/**
 * Format exam time only (no date) for student display.
 * Shows local timezone + IST reference for non-Indian students.
 *
 * Examples:
 *   Indian student:  "12:01 AM IST"
 *   Saudi student:   "9:31 PM AST (12:01 AM IST)"
 */
export function formatExamTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  const tzAbbr = getLocalTzAbbr();

  const localStr = date.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (isIST()) {
    return `${localStr} IST`;
  }

  const istStr = formatInIST(date, false);
  return `${localStr} ${tzAbbr} (${istStr})`;
}

/**
 * Short format for exam lists: "9 Apr, 12:01 AM IST"
 * No IST reference for non-Indian (keeps it compact for list views).
 */
export function formatExamDateShort(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  const tzAbbr = getLocalTzAbbr();

  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }) + ` ${tzAbbr}`;
}
